import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import { assetClassLabel } from '../../../shared/portfolio';
import { formatAUD, formatPct, formatDateAU } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { QASkeleton } from './QASkeleton';
import { QAChartTooltip } from './QAChartTooltip';
import type {
  PerformanceResponse, CgtResponse, IncomeResponse, PortfolioSnapshot,
  PortfolioAccount, PortfolioHolding,
} from '../../../shared/api';
import type { AllocationSlice } from '../../../shared/portfolio';

const PERIODS: Array<[string, string]> = [
  ['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['1y', '1Y'], ['3y', '3Y'],
  ['5y', '5Y'], ['fytd', 'FYTD'], ['cytd', 'CYTD'], ['itd', 'ITD'],
];

const QA_COLOURS = ['#B8431A', '#A07A3F', '#2F4636', '#8A6418', '#7A6E63', '#3D3530'];

function pct(v: number | null | undefined): string {
  return v == null ? '—' : formatPct(v, 2);
}

export function PerformanceTab({ clientId, portfolio }: { clientId: string; portfolio: PortfolioSnapshot }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = can(user?.role, 'users.manage');
  const [selection, setSelection] = useState<string>('consolidated'); // 'consolidated' | accountId
  const [period, setPeriod] = useState<string>('itd');

  const base = selection === 'consolidated'
    ? `/api/clients/${clientId}`
    : `/api/clients/${clientId}/accounts/${selection}`;

  const perfQ = useQuery({ queryKey: ['perf', clientId, selection], queryFn: () => api<{ data: PerformanceResponse }>(`${base}/performance`) });
  const cgtQ = useQuery({ queryKey: ['perf-cgt', clientId, selection], queryFn: () => api<{ data: CgtResponse }>(`${base}/cgt`) });
  const incomeQ = useQuery({ queryKey: ['perf-income', clientId, selection], queryFn: () => api<{ data: IncomeResponse }>(`${base}/income`) });

  // Pending feed-reconciliation count — admin-only, used to surface a badge
  // + link to /admin/feed-reconciliation. Stays quiet for non-managers.
  const reconcileQ = useQuery({
    queryKey: ['feed-pending-count'],
    queryFn: () => api<{ data: { count: number } }>('/api/admin/feed-reconciliation/pending-count'),
    enabled: canManage,
    staleTime: 60_000,
  });
  const pendingCount = reconcileQ.data?.data.count ?? 0;

  const refresh = useMutation({
    mutationFn: () => api('/api/integrations/market/refresh', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries(),
  });

  const sync = useMutation({
    mutationFn: () => api<{ data: { conflicts?: number } }>('/api/admin/feed-reconciliation/sync', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['perf'] });
      qc.invalidateQueries({ queryKey: ['perf-cgt'] });
      qc.invalidateQueries({ queryKey: ['perf-income'] });
      qc.invalidateQueries({ queryKey: ['feed-pending-count'] });
    },
  });

  const perf = perfQ.data?.data;

  // Selected account, when one is chosen (vs consolidated).
  const selectedAccount: PortfolioAccount | null = useMemo(() => {
    if (selection === 'consolidated') return null;
    return portfolio.accounts.find((a) => a.id === selection) ?? null;
  }, [selection, portfolio]);

  const allocationSlices: AllocationSlice[] = useMemo(() => {
    const raw = selectedAccount ? selectedAccount.allocation : portfolio.allocation;
    return (raw ?? []).map((s) => ({ ...s, bucket: assetClassLabel(s.bucket) }));
  }, [selectedAccount, portfolio]);

  const geographySlices: AllocationSlice[] = useMemo(() => {
    return selectedAccount ? (selectedAccount.geography ?? []) : (portfolio.geography ?? []);
  }, [selectedAccount, portfolio]);

  const holdingRows: Array<PortfolioHolding & { accountName?: string }> = useMemo(() => {
    if (selectedAccount) return selectedAccount.holdings;
    return portfolio.accounts.flatMap((a) => a.holdings.map((h) => ({ ...h, accountName: a.name })));
  }, [selectedAccount, portfolio]);

  // Indexed-to-100 chart: portfolio vs benchmark, sliced to the selected period.
  const chartData = useMemo(() => {
    if (!perf) return [];
    const start = perf.periods[period]?.startDate ?? null;
    const series = perf.series.filter((p) => !start || p.date >= start);
    if (series.length === 0) return [];
    const p0 = series[0].valueCents || 1;
    const bm = new Map((perf.benchmark?.series ?? []).map((b) => [b.date, b.valueCents]));
    const bmStart = perf.benchmark?.series.find((b) => !start || b.date >= start)?.valueCents ?? null;
    return series.map((p) => {
      const bmv = bm.get(p.date);
      return {
        date: p.date,
        portfolio: Math.round((p.valueCents / p0) * 1000) / 10,
        benchmark: bmv != null && bmStart ? Math.round((bmv / bmStart) * 1000) / 10 : null,
      };
    });
  }, [perf, period]);

  if (perfQ.isLoading) return <QASkeleton variant="cards" />;
  if (perfQ.isError || !perf) return <div className="qa-badge qa-badge--out mt-6">Unable to load performance</div>;

  const pr = perf.periods[period];
  const bmTwr = perf.benchmark?.periods[period]?.twr ?? null;
  const excess = pr?.twr != null && bmTwr != null ? pr.twr - bmTwr : null;

  return (
    <div className="mt-6 space-y-8">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <select
          className="qa-input text-sm"
          value={selection}
          onChange={(e) => setSelection(e.target.value)}
          aria-label="Portfolio"
        >
          <option value="consolidated">Consolidated household</option>
          {portfolio.accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && pendingCount > 0 ? (
            <Link to="/admin/feed-reconciliation"
              className="text-[11px] uppercase font-bold tracking-widest border border-rust text-rust px-2.5 py-1 hover:bg-rust hover:text-paper transition-colors"
              aria-label={`${pendingCount} pending feed conflicts — open reconciliation`}>
              Reconcile ({pendingCount})
            </Link>
          ) : null}
          {canManage ? (
            <button type="button" className="qa-btn qa-btn--ghost text-sm" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {sync.isPending ? 'Syncing…' : 'Sync positions'}
            </button>
          ) : null}
          <button type="button" className="qa-btn qa-btn--ghost text-sm" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh prices'}
          </button>
        </div>
      </div>

      {/* Period pills */}
      <div className="flex flex-wrap gap-2">
        {PERIODS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setPeriod(k)}
            className={`px-2.5 py-1 text-[11px] uppercase font-bold tracking-widest border ${period === k ? 'border-rust text-rust' : 'border-hairline text-ink-mute hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Return figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Return (TWR)" value={pct(pr?.twr)} note={pr?.days ? `${pr.days} days` : ''} />
        <Stat label="Return (MWR/IRR)" value={pct(pr?.mwr)} />
        <Stat label="Benchmark (TWR)" value={pct(bmTwr)} note={perf.benchmark?.name ?? ''} />
        <Stat label="Excess vs benchmark" value={pct(excess)} tone={excess == null ? undefined : excess >= 0 ? 'pos' : 'neg'} />
      </div>

      {/* Chart */}
      <div className="qa-card p-6">
        <p className="qa-eyebrow mb-3">Growth of $100 — portfolio vs benchmark</p>
        {chartData.length < 2 ? (
          <QAEmpty inline title="Not enough valuation history yet.">The nightly valuation job builds this series over time.</QAEmpty>
        ) : (
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="rgba(26,22,20,0.08)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => formatDateAU(String(d))} minTickGap={40} />
                <YAxis tick={{ fontSize: 11 }} width={40} />
                <Tooltip content={<QAChartTooltip valueFormat={(v) => (typeof v === 'number' ? v.toFixed(1) : String(v))} />} />
                <Legend />
                <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="#B8431A" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="benchmark" name="Benchmark" stroke="#A07A3F" dot={false} strokeWidth={1.5} strokeDasharray="4 3" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Allocation + geography pies (scoped to selection) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <AllocationCard title="Asset allocation" slices={allocationSlices} />
        <AllocationCard title="Geography" slices={geographySlices} />
      </div>

      {/* Holdings (scoped to selection) */}
      <div className="qa-card overflow-hidden">
        <header className="px-5 py-3 flex items-baseline justify-between">
          <p className="qa-eyebrow">{selectedAccount ? `Holdings — ${selectedAccount.name}` : 'Holdings — all accounts'}</p>
          <span className="text-xs text-ink-mute qa-mono">{holdingRows.length} positions</span>
        </header>
        {holdingRows.length === 0 ? (
          <div className="px-5 pb-5"><QAEmpty inline title="No holdings to show." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="qa-table">
              <thead>
                <tr>
                  <th>Security</th>
                  {!selectedAccount && <th>Account</th>}
                  <th>Class</th>
                  <th className="text-right">Units</th>
                  <th className="text-right">Price</th>
                  <th className="text-right">Value</th>
                  <th className="text-right">U/G/L</th>
                  <th className="text-right">Return</th>
                </tr>
              </thead>
              <tbody>
                {holdingRows.map((h, i) => (
                  <tr key={`${h.code}-${i}`}>
                    <td><span className="font-medium">{h.code}</span> <span className="text-ink-mute">{h.name}</span></td>
                    {!selectedAccount && <td className="text-xs text-ink-mute">{h.accountName}</td>}
                    <td className="text-xs text-ink-mute">{assetClassLabel(h.assetClass)}</td>
                    <td className="num">{h.units.toLocaleString('en-AU')}</td>
                    <td className="num">{formatAUD(h.currentPrice)}</td>
                    <td className="num">{formatAUD(h.currentValue)}</td>
                    <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                      {h.unrealisedGainLoss >= 0 ? '+' : ''}{formatAUD(h.unrealisedGainLoss)}
                    </td>
                    <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                      {h.unrealisedReturnPct >= 0 ? '+' : ''}{formatPct(h.unrealisedReturnPct)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="qa-hairline-t font-medium">
                  <td colSpan={selectedAccount ? 4 : 5}>Total holdings</td>
                  <td className="num">{formatAUD(holdingRows.reduce((s, h) => s + h.currentValue, 0))}</td>
                  <td></td><td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* CGT + income */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="qa-card p-6">
          <p className="qa-eyebrow mb-3">Capital gains</p>
          {cgtQ.isLoading ? <QASkeleton variant="cards" /> : cgtQ.data ? (
            <CgtPanel cgt={cgtQ.data.data} />
          ) : <p className="text-ink-mute text-sm">Unavailable.</p>}
        </div>
        <div className="qa-card p-6">
          <p className="qa-eyebrow mb-3">Income &amp; franking</p>
          {incomeQ.isLoading ? <QASkeleton variant="cards" /> : incomeQ.data ? (
            <IncomePanel income={incomeQ.data.data} />
          ) : <p className="text-ink-mute text-sm">Unavailable.</p>}
        </div>
      </div>
    </div>
  );
}

function AllocationCard({ title, slices }: { title: string; slices: AllocationSlice[] }) {
  if (!slices || slices.length === 0) {
    return (
      <div className="qa-card p-6">
        <p className="qa-eyebrow mb-3">{title}</p>
        <QAEmpty inline title="No data yet." />
      </div>
    );
  }
  return (
    <div className="qa-card p-6">
      <p className="qa-eyebrow mb-3">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
        <div className="h-48">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={slices} dataKey="value" nameKey="bucket" innerRadius={45} outerRadius={80} stroke="#FBF7EF">
                {slices.map((_, i) => (
                  <Cell key={i} fill={QA_COLOURS[i % QA_COLOURS.length]} />
                ))}
              </Pie>
              <Tooltip content={<QAChartTooltip valueFormat={(v, entry) => `${formatAUD(Number(v))}${entry.payload?.pct != null ? ` · ${formatPct(entry.payload.pct as number)}` : ''}`} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="space-y-1 text-sm">
          {slices.map((slice, i) => (
            <li key={slice.bucket} className="flex justify-between qa-mono">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-2 h-2" style={{ background: QA_COLOURS[i % QA_COLOURS.length] }} />
                {slice.bucket}
              </span>
              <span>{formatPct(slice.pct)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'pos' ? 'text-forest' : tone === 'neg' ? 'text-rust' : 'text-ink';
  return (
    <div className="qa-card p-4">
      <p className="qa-eyebrow">{label}</p>
      <p className={`text-2xl font-display mt-1 ${colour}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      {note ? <p className="text-[11px] text-ink-mute mt-0.5">{note}</p> : null}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'pos' ? 'text-forest' : tone === 'neg' ? 'text-rust' : 'text-ink';
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-hairline last:border-0">
      <span className="text-sm text-ink-mute">{label}</span>
      <span className={`text-sm font-mono ${colour}`}>{value}</span>
    </div>
  );
}

function CgtPanel({ cgt }: { cgt: CgtResponse }) {
  const u = cgt.unrealised.summary;
  const r = cgt.realised.summary;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-ink-mute mb-1">Unrealised</p>
      <Row label="Market value" value={formatAUD(u.marketCents / 100)} />
      <Row label="Cost base" value={formatAUD(u.costCents / 100)} />
      <Row label="Gain / loss" value={formatAUD(u.gainCents / 100)} tone={u.gainCents >= 0 ? 'pos' : 'neg'} />
      <Row label="Discount-eligible gain" value={formatAUD(u.discountEligibleGainCents / 100)} />
      <p className="text-[11px] uppercase tracking-widest text-ink-mute mt-4 mb-1">Realised</p>
      <Row label="Net gain / loss" value={formatAUD(r.gainCents / 100)} tone={r.gainCents >= 0 ? 'pos' : 'neg'} />
      <Row label="Discounted gains" value={formatAUD(r.discountedGainCents / 100)} />
      <Row label="Other gains" value={formatAUD(r.otherGainCents / 100)} />
      <Row label="Losses" value={formatAUD(r.lossCents / 100)} tone={r.lossCents < 0 ? 'neg' : undefined} />
    </div>
  );
}

function IncomePanel({ income }: { income: IncomeResponse }) {
  const s = income.summary;
  return (
    <div>
      <Row label="Cash income" value={formatAUD(s.cashAudCents / 100)} />
      <Row label="Franking credits" value={formatAUD(s.frankingCreditsAudCents / 100)} />
      <Row label="Grossed-up total" value={formatAUD(s.grossedUpAudCents / 100)} tone="pos" />
      <Row label="Events" value={String(s.count)} />
      {income.events.length > 0 && (
        <ul className="mt-3 text-xs text-ink-mute space-y-1">
          {income.events.slice(0, 6).map((e, i) => (
            <li key={i} className="flex justify-between">
              <span>{e.payDate ? formatDateAU(e.payDate) : '—'} · {e.securityCode ?? e.incomeType}</span>
              <span className="font-mono">{formatAUD(e.cashAudCents / 100)}{e.frankingCreditsAudCents > 0 ? ` (+${formatAUD(e.frankingCreditsAudCents / 100)} fr)` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
